import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

import { statusMap } from "./match/constants";
import MatchHeader from "./match/MatchHeader";
import MatchPhoto from "./match/MatchPhoto";
import TeamCardButton from "./match/TeamCardButton";
import ShareMatchButton from "./match/ShareMatchButton";
import RecapCardButton from "./match/RecapCardButton";
import TabBtn from "./match/TabBtn";
import PresenceTab from "./match/PresenceTab";
import RosterTab from "./match/RosterTab";
import StatsTab from "./match/StatsTab";
import GalaView from "./match/GalaView";
import ConvocationsTab from "./match/ConvocationsTab";

export default function MatchDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, settings } = useAuth();
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("presence"); // presence | roster | sheet
  const [rosterDraft, setRosterDraft] = useState({}); // {player_id: "bleus" | "rouges" | null}
  const [captainDraft, setCaptainDraft] = useState({ bleus: null, rouges: null });
  const [sheet, setSheet] = useState({
    score_bleus: 0,
    score_rouges: 0,
    our_score: 0,
    opponent_score: 0,
    summary: "",
    man_of_the_match: null,
    stats: {},
  });

  const isCoach = user?.role === "coach" || user?.role === "admin";
  const canManage = isCoach || !!user?.is_comms_officer;
  const isOfficial = match?.match_type === "official";
  const isGala = match?.match_type === "gala";

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/matches/${id}`);
      setMatch(data);

      const rMap = {};
      (data.rosters || []).forEach((r) => (rMap[r.player_id] = r.side));
      setRosterDraft(rMap);
      setCaptainDraft({
        bleus: data.bleus_captain_id || null,
        rouges: data.rouges_captain_id || null,
      });

      const statsById = {};
      const isOff = data.match_type === "official";
      if (isOff) {
        const avail = (data.availabilities || []).filter((a) => a.status === "available");
        avail.forEach((a) => {
          const existing = (data.stats || []).find((s) => s.player_id === a.player_id);
          statsById[a.player_id] = existing
            ? {
                side: "us",
                goals: existing.goals,
                assists: existing.assists,
                yellow_cards: existing.yellow_cards,
                red_cards: existing.red_cards,
                observation: existing.observation || "",
                played: existing.played,
              }
            : {
                side: "us",
                goals: 0,
                assists: 0,
                yellow_cards: 0,
                red_cards: 0,
                observation: "",
                played: true,
              };
        });
        (data.stats || []).forEach((s) => {
          if (!statsById[s.player_id]) {
            statsById[s.player_id] = {
              side: "us",
              goals: s.goals,
              assists: s.assists,
              yellow_cards: s.yellow_cards,
              red_cards: s.red_cards,
              observation: s.observation || "",
              played: s.played,
            };
          }
        });
      } else {
        (data.rosters || []).forEach((r) => {
          const existing = (data.stats || []).find((s) => s.player_id === r.player_id);
          statsById[r.player_id] = existing
            ? {
                side: existing.side,
                goals: existing.goals,
                assists: existing.assists,
                yellow_cards: existing.yellow_cards,
                red_cards: existing.red_cards,
                observation: existing.observation || "",
                played: existing.played,
              }
            : {
                side: r.side,
                goals: 0,
                assists: 0,
                yellow_cards: 0,
                red_cards: 0,
                observation: "",
                played: true,
              };
        });
      }
      setSheet({
        score_bleus: data.score_bleus ?? 0,
        score_rouges: data.score_rouges ?? 0,
        our_score: data.our_score ?? 0,
        opponent_score: data.opponent_score ?? 0,
        team_captain_id: data.team_captain_id || null,
        summary: data.summary || "",
        man_of_the_match: data.man_of_the_match || null,
        stats: statsById,
      });
    } catch (e) {
      toast.error("Match introuvable");
      navigate("/matches");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  const availByPlayer = useMemo(() => {
    const m = {};
    (match?.availabilities || []).forEach((a) => (m[a.player_id] = a));
    return m;
  }, [match]);

  const rosterByPlayer = useMemo(() => {
    const m = {};
    (match?.rosters || []).forEach((r) => (m[r.player_id] = r.side));
    return m;
  }, [match]);

  const myAvail = availByPlayer[user?.id]?.status;

  const setSelfAvailability = async (status) => {
    try {
      await api.post(`/matches/${id}/availability`, { status });
      toast.success(`Disponibilité : ${statusMap[status].label}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const setPlayerAvailability = async (playerId, status) => {
    try {
      await api.post(`/matches/${id}/availability/coach?player_id=${playerId}`, { status });
      toast.success("Mis à jour");
      load();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const setDraftSide = (playerId, side) => {
    setRosterDraft((r) => ({ ...r, [playerId]: side }));
  };

  const saveRoster = async () => {
    try {
      const assignments = Object.entries(rosterDraft)
        .filter(([, side]) => side === "bleus" || side === "rouges")
        .map(([player_id, side]) => ({ player_id, side }));
      await api.post(`/matches/${id}/roster`, { assignments });
      toast.success("Composition enregistrée");
      load();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const saveCaptains = async () => {
    try {
      await api.post(`/matches/${id}/captains`, {
        bleus_captain_id: captainDraft.bleus || null,
        rouges_captain_id: captainDraft.rouges || null,
      });
      toast.success("Capitaines enregistrés");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const balance = () => {
    const assignableIds = (match?.availabilities || [])
      .filter((a) => a.status === "available" || a.status === "pending")
      .map((a) => a.player_id);
    const presentPlayers = assignableIds
      .map((pid) => (match?.players || []).find((p) => p.id === pid))
      .filter(Boolean);

    const buckets = { def: [], mid: [], att: [], gk: [], other: [] };
    presentPlayers.forEach((p) => {
      const pos = (p.position || "").toLowerCase();
      if (/gard|goal|keeper|gk/.test(pos)) buckets.gk.push(p);
      else if (/def|arri|back/.test(pos)) buckets.def.push(p);
      else if (/mid|milieu/.test(pos)) buckets.mid.push(p);
      else if (/att|for|str|ail/.test(pos)) buckets.att.push(p);
      else buckets.other.push(p);
    });

    const next = {};
    let toggle = Math.random() > 0.5 ? 0 : 1;
    Object.values(buckets).forEach((bucket) => {
      const shuffled = [...bucket].sort(() => Math.random() - 0.5);
      shuffled.forEach((p) => {
        next[p.id] = toggle % 2 === 0 ? "bleus" : "rouges";
        toggle += 1;
      });
    });
    setRosterDraft(next);
    toast.info("Répartition équilibrée par poste · pensez à enregistrer");
  };

  const updateStat = (playerId, field, value) => {
    setSheet((s) => ({
      ...s,
      stats: { ...s.stats, [playerId]: { ...s.stats[playerId], [field]: value } },
    }));
  };

  const submitSheet = async () => {
    try {
      const payload = {
        summary: sheet.summary,
        man_of_the_match: sheet.man_of_the_match || null,
      };
      if (isOfficial) {
        payload.our_score = Number(sheet.our_score) || 0;
        payload.opponent_score = Number(sheet.opponent_score) || 0;
        payload.team_captain_id = sheet.team_captain_id || null;
        payload.stats = Object.entries(sheet.stats).map(([pid, s]) => ({
          player_id: pid,
          side: "us",
          goals: Number(s.goals) || 0,
          assists: Number(s.assists) || 0,
          yellow_cards: Number(s.yellow_cards) || 0,
          red_cards: Number(s.red_cards) || 0,
          observation: s.observation || "",
          played: !!s.played,
        }));
      } else {
        payload.score_bleus = Number(sheet.score_bleus) || 0;
        payload.score_rouges = Number(sheet.score_rouges) || 0;
        payload.bleus_captain_id = match.bleus_captain_id || null;
        payload.rouges_captain_id = match.rouges_captain_id || null;
        payload.stats = Object.entries(sheet.stats)
          .filter(([, s]) => s && (s.side === "bleus" || s.side === "rouges"))
          .map(([pid, s]) => ({
            player_id: pid,
            side: s.side,
            goals: Number(s.goals) || 0,
            assists: Number(s.assists) || 0,
            yellow_cards: Number(s.yellow_cards) || 0,
            red_cards: Number(s.red_cards) || 0,
            observation: s.observation || "",
            played: !!s.played,
          }));
      }
      await api.post(`/matches/${id}/sheet`, payload);
      toast.success("Feuille de match enregistrée");
      load();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const exportPDF = () => window.print();

  if (loading || !match) {
    return <div className="text-slate-400 text-sm font-mono-stat uppercase">Chargement...</div>;
  }

  const present = (match.availabilities || []).filter((a) => a.status === "available");
  // Assignable to teams: confirmed (available) + incertain (pending). Both can be split by the coach.
  const assignable = (match.availabilities || []).filter(
    (a) => a.status === "available" || a.status === "pending"
  );

  // Gala: dedicated view (score-only, custom team names, external guests)
  if (isGala) {
    return (
      <div className="space-y-6 print-container">
        <button
          data-testid="back-button"
          onClick={() => navigate(-1)}
          className="no-print inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white uppercase tracking-wide font-semibold"
        >
          <ArrowLeft className="w-4 h-4" /> Retour
        </button>

        <GalaView match={match} isCoach={canManage} onReload={load} />

        {/* Availability quick actions for gala */}
        <PresenceTab
          match={match}
          isCoach={isCoach}
          myAvail={myAvail}
          availByPlayer={availByPlayer}
          present={present}
          setSelfAvailability={setSelfAvailability}
          setPlayerAvailability={setPlayerAvailability}
        />

        {/* Photos: always available on gala (avant, pendant ou après l'événement) */}
        <MatchPhoto match={match} isCoach={canManage} onChange={load} />
      </div>
    );
  }

  return (
    <div className="space-y-6 print-container">
      <button
        data-testid="back-button"
        onClick={() => navigate(-1)}
        className="no-print inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white uppercase tracking-wide font-semibold"
      >
        <ArrowLeft className="w-4 h-4" /> Retour
      </button>

      <MatchHeader match={match} present={present} settings={settings} />

      {match.status === "finished" && (
        <MatchPhoto match={match} isCoach={canManage} onChange={load} />
      )}

      {/* Tabs */}
      <div className="no-print flex flex-wrap gap-2 items-center justify-between">
        <div className="flex flex-wrap gap-2 p-1 rounded-xl bg-slate-900 border border-white/10">
          <TabBtn active={tab === "presence"} onClick={() => setTab("presence")} testid="tab-presence">
            {isOfficial ? "1. Convocation" : "1. Présence"}
          </TabBtn>
          {!isOfficial && (
            <TabBtn active={tab === "roster"} onClick={() => setTab("roster")} testid="tab-roster">
              2. Répartition
            </TabBtn>
          )}
          <TabBtn active={tab === "sheet"} onClick={() => setTab("sheet")} testid="tab-sheet">
            {isOfficial ? "2. Feuille de match" : "3. Feuille de match"}
          </TabBtn>
        </div>
        <div className="flex gap-2 flex-wrap">
          {match.status !== "finished" && <ShareMatchButton match={match} />}
          {match.status === "finished" && <RecapCardButton match={match} />}
          <TeamCardButton match={match} />
          {match.status === "finished" && (
            <button
              data-testid="export-pdf-button"
              onClick={exportPDF}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-white font-semibold uppercase tracking-wide rounded-lg text-sm"
            >
              <Printer className="w-4 h-4" /> Export PDF
            </button>
          )}
        </div>
      </div>

      {tab === "presence" && !isOfficial && (
        <PresenceTab
          match={match}
          isCoach={isCoach}
          myAvail={myAvail}
          availByPlayer={availByPlayer}
          present={present}
          setSelfAvailability={setSelfAvailability}
          setPlayerAvailability={setPlayerAvailability}
        />
      )}

      {tab === "presence" && isOfficial && (
        <>
          {/* Player convocation response — only visible to convoked players */}
          {!isCoach && availByPlayer[user?.id]?.is_convoked && match.status !== "finished" && (
            <div className="rounded-2xl bg-gradient-to-br from-[#E5B85C]/10 to-slate-900 border border-[#E5B85C]/40 p-5">
              <div className="text-[11px] font-mono-stat uppercase tracking-widest text-[#E5B85C] mb-2">
                Tu es convoqué(e) !
              </div>
              <div className="font-display font-bold text-xl mb-3">Confirme ta présence</div>
              <div className="flex flex-wrap gap-2">
                <button
                  data-testid="convocation-confirm"
                  onClick={() => setSelfAvailability("available")}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-bold uppercase tracking-wide ${
                    myAvail === "available"
                      ? "bg-emerald-500/30 border-emerald-500 text-emerald-100"
                      : "bg-emerald-500/10 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/20"
                  }`}
                >
                  Je confirme
                </button>
                <button
                  data-testid="convocation-decline"
                  onClick={() => setSelfAvailability("unavailable")}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-bold uppercase tracking-wide ${
                    myAvail === "unavailable"
                      ? "bg-rose-500/30 border-rose-500 text-rose-100"
                      : "bg-rose-500/10 border-rose-500/40 text-rose-300 hover:bg-rose-500/20"
                  }`}
                >
                  Je décline
                </button>
              </div>
            </div>
          )}
          {!isCoach && !availByPlayer[user?.id]?.is_convoked && match.status !== "finished" && (
            <div className="p-5 rounded-2xl bg-slate-900 border border-white/10 text-slate-300">
              <div className="text-[11px] font-mono-stat uppercase tracking-widest text-slate-400 mb-1">
                Match officiel
              </div>
              Seuls les joueurs convoqués par le coach participent aux matchs officiels.
              Tu recevras une notification si tu es sélectionné(e).
            </div>
          )}
          <ConvocationsTab match={match} isCoach={isCoach} onReload={load} />
        </>
      )}

      {tab === "roster" && !isOfficial && (
        <RosterTab
          match={match}
          isCoach={isCoach}
          present={assignable}
          rosterDraft={rosterDraft}
          setDraftSide={setDraftSide}
          saveRoster={saveRoster}
          balance={balance}
          captainDraft={captainDraft}
          setCaptainDraft={setCaptainDraft}
          saveCaptains={saveCaptains}
          rosterByPlayer={rosterByPlayer}
        />
      )}

      {tab === "sheet" && (
        <StatsTab
          match={match}
          sheet={sheet}
          setSheet={setSheet}
          isCoach={canManage}
          updateStat={updateStat}
          submitSheet={submitSheet}
        />
      )}

      {match.summary && tab !== "sheet" && (
        <div className="rounded-2xl bg-slate-900 border border-white/10 p-5">
          <div className="text-[10px] font-mono-stat uppercase tracking-widest text-[#E5B85C] mb-2">
            Résumé
          </div>
          <div className="text-slate-300 text-sm leading-relaxed">{match.summary}</div>
        </div>
      )}
    </div>
  );
}
